/**
 * Overview page — per docs/specs/overview.md
 *
 * @copyright 2025 Glimbr Pty Ltd
 * @version 2026-04-22-v1
 */
import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
    AlertCircle,
    AlertTriangle,
    ArrowRight,
    CheckCircle,
    CreditCard,
    Download,
    Info,
    LifeBuoy,
    Radio,
    RefreshCw,
    ShieldAlert,
    Upload,
    UserPlus
} from 'lucide-react';
import {
    Area,
    AreaChart,
    CartesianGrid,
    Line,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis
} from 'recharts';
import { KpiTile } from '../components/kpi-tile';
import { useToast } from '../components/toast';
import {
    useActivity,
    useAlerts,
    useBillingKpis,
    useCharts,
    useKpis,
    usePulse
} from '../../lib/api';
import { exportCsv } from '../../lib/csv';
import type { ActivityCategory, Severity } from '../../lib/types';
import {
    DEFAULT_OVERVIEW_DATE_RANGE,
    overviewDateRangeDays,
    parseOverviewDateRange,
    type OverviewDateRange
} from './overview-filters';

export function OverviewPage() {
    const [params, setParams] = useSearchParams();
    const dateRange = parseOverviewDateRange(params.get('days'));
    const days = overviewDateRangeDays(dateRange);
    const kpis = useKpis(days);
    const billingKpis = useBillingKpis();
    const alerts = useAlerts();
    const activity = useActivity();
    const pulse = usePulse();
    const charts = useCharts(days);
    const qc = useQueryClient();
    const { toast } = useToast();
    const mergedKpis = useMemo(() => {
        const billing = billingKpis.data;
        if (!kpis.data) return [];
        return kpis.data.map((kpi) => {
            if (kpi.id === 'mrr' && billing?.mrr) return billing.mrr;
            if (kpi.id === 'failed-payments' && billing?.failed) {
                return {
                    ...kpi,
                    value: billing.failed.value,
                    delta: billing.failed.delta,
                    spark: billing.failed.spark,
                    href: kpi.href,
                    alert: kpi.alert ?? billing.failed.alert
                };
            }
            return kpi;
        });
    }, [billingKpis.data, kpis.data]);

    const updateRange = (value: OverviewDateRange) => {
        const next = new URLSearchParams(params);
        if (value === DEFAULT_OVERVIEW_DATE_RANGE) next.delete('days');
        else next.set('days', value);
        setParams(next, { replace: true });
    };

    const handleRefresh = async () => {
        await Promise.all([
            qc.invalidateQueries({ queryKey: ['admin', 'overview', 'kpis', days] }),
            qc.invalidateQueries({ queryKey: ['admin', 'billing', 'kpis'] }),
            qc.invalidateQueries({ queryKey: ['admin', 'overview', 'alerts'] }),
            qc.invalidateQueries({ queryKey: ['admin', 'overview', 'activity'] }),
            qc.invalidateQueries({ queryKey: ['admin', 'overview', 'pulse'] }),
            qc.invalidateQueries({ queryKey: ['admin', 'overview', 'charts', days] })
        ]);
        toast('Overview refreshed', { kind: 'info' });
    };

    const handleExport = () => {
        const rows = [
            ...mergedKpis.map((k) => ({
                section: 'kpi',
                label: k.label,
                value: k.value,
                delta: k.delta.pct,
                href: k.href
            })),
            ...(alerts.data ?? []).map((a) => ({
                section: 'alert',
                label: a.message,
                value: a.severity,
                delta: a.meta ?? '',
                href: a.href
            })),
            ...(pulse.data ?? []).map((p) => ({
                section: 'pulse',
                label: p.name,
                value: p.status,
                delta: p.metric,
                href: ''
            })),
            ...(charts.data?.signups ?? []).map((point) => ({
                section: 'signup',
                label: point.date,
                value: point.count,
                delta: point.ma7,
                href: ''
            })),
            ...(charts.data?.top_errors ?? []).map((error) => ({
                section: 'error',
                label: error.code,
                value: error.count,
                delta: error.sample,
                href: `/error-logs?code=${encodeURIComponent(error.code)}`
            }))
        ];
        exportCsv(`overview-${dateRange}-${new Date().toISOString().slice(0, 10)}`, rows);
        toast('Overview export ready');
    };

    return (
        <div className="stack-16">
            <header className="page-header">
                <div className="page-title-group">
                    <h1>Overview</h1>
                    <p>Live state across Producer Dashboard — alerts, KPIs, activity, pulse.</p>
                </div>
                <div className="page-actions">
                    <select
                        className="select"
                        value={dateRange}
                        aria-label="Date range"
                        onChange={(event) => updateRange(event.target.value as OverviewDateRange)}
                    >
                        <option value="7d">Last 7 days</option>
                        <option value="30d">Last 30 days</option>
                        <option value="90d">Last 90 days</option>
                    </select>
                    <button
                        className="btn btn-ghost"
                        type="button"
                        onClick={() => void handleRefresh()}
                    >
                        <RefreshCw size={14} /> Refresh
                    </button>
                    <button className="btn btn-ghost" type="button" onClick={handleExport}>
                        <Download size={14} /> Export
                    </button>
                </div>
            </header>

            {alerts.data && alerts.data.length > 0 && (
                <section className="alert-bar">
                    {alerts.data.map((a) => (
                        <Link key={a.id} to={a.href} className="alert" data-severity={a.severity}>
                            <AlertIcon severity={a.severity} />
                            <span className="msg">{a.message}</span>
                            {a.meta && <span className="meta">{a.meta}</span>}
                            <ArrowRight size={14} />
                        </Link>
                    ))}
                </section>
            )}

            <section className="kpi-grid" aria-label="Key metrics">
                {mergedKpis.map((k) => (
                    <KpiTile key={k.id} kpi={k} />
                ))}
            </section>

            <div className="grid-split">
                <div className="stack-12">
                    <section className="card">
                        <header className="card-header">
                            <h2 className="card-title">Signups · {dateRange}</h2>
                            <span className="text-muted" style={{ fontSize: 11 }}>
                                Daily count + 7-day moving avg
                            </span>
                        </header>
                        <div className="card-body" style={{ height: 240 }}>
                            {charts.data && (
                                <ResponsiveContainer width="100%" height="100%">
                                    <AreaChart data={charts.data.signups}>
                                        <defs>
                                            <linearGradient id="sg" x1="0" y1="0" x2="0" y2="1">
                                                <stop
                                                    offset="0%"
                                                    stopColor="#3b82f6"
                                                    stopOpacity={0.45}
                                                />
                                                <stop
                                                    offset="100%"
                                                    stopColor="#3b82f6"
                                                    stopOpacity={0}
                                                />
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid
                                            vertical={false}
                                            stroke="rgba(255,255,255,0.04)"
                                        />
                                        <XAxis
                                            dataKey="date"
                                            tickLine={false}
                                            axisLine={false}
                                            tick={{
                                                fontSize: 10,
                                                fill: 'rgba(255,255,255,0.4)'
                                            }}
                                            minTickGap={32}
                                        />
                                        <YAxis
                                            tickLine={false}
                                            axisLine={false}
                                            width={28}
                                            tick={{
                                                fontSize: 10,
                                                fill: 'rgba(255,255,255,0.4)'
                                            }}
                                        />
                                        <Tooltip
                                            contentStyle={{
                                                background: '#12121a',
                                                border: '1px solid rgba(255,255,255,0.1)',
                                                borderRadius: 8,
                                                fontSize: 12
                                            }}
                                            labelStyle={{ color: 'rgba(255,255,255,0.6)' }}
                                        />
                                        <Area
                                            type="monotone"
                                            dataKey="count"
                                            stroke="#3b82f6"
                                            strokeWidth={2}
                                            fill="url(#sg)"
                                        />
                                        <Line
                                            type="monotone"
                                            dataKey="ma7"
                                            stroke="#a855f7"
                                            strokeWidth={1.5}
                                            strokeDasharray="3 3"
                                            dot={false}
                                        />
                                    </AreaChart>
                                </ResponsiveContainer>
                            )}
                        </div>
                    </section>

                    <section className="card">
                        <header className="card-header">
                            <h2 className="card-title">Live activity · last 10 min</h2>
                            <Link to="/analytics" style={{ fontSize: 11 }}>
                                See all →
                            </Link>
                        </header>
                        <div className="card-body tight">
                            <div className="activity">
                                {activity.data?.map((e) => (
                                    <Link
                                        to={e.href}
                                        key={e.id}
                                        className="activity-row"
                                        style={{ color: 'inherit', textDecoration: 'none' }}
                                    >
                                        <span className="ts">{e.ts}</span>
                                        <span
                                            className="icon"
                                            data-tone={toneForCategory(e.category)}
                                        >
                                            <ActivityIcon category={e.category} />
                                        </span>
                                        <span className="msg">
                                            <span className="actor">{e.actor}</span> {e.message}
                                            {e.context && (
                                                <span className="context"> — {e.context}</span>
                                            )}
                                        </span>
                                    </Link>
                                ))}
                            </div>
                        </div>
                    </section>
                </div>

                <div className="stack-12">
                    <section className="card">
                        <header className="card-header">
                            <h2 className="card-title">System pulse</h2>
                            <span className="text-muted" style={{ fontSize: 11 }}>
                                30s refresh
                            </span>
                        </header>
                        <div className="card-body tight">
                            <div className="pulse-list">
                                {pulse.data?.map((p) => (
                                    <div key={p.id} className="pulse-row">
                                        <span className="pulse-dot" data-status={p.status} />
                                        <span className="name">{p.name}</span>
                                        <span className="metric">{p.metric}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </section>

                    <section className="card">
                        <header className="card-header">
                            <h2 className="card-title">Revenue · {dateRange}</h2>
                        </header>
                        <div className="card-body">
                            {charts.data && (
                                <dl className="kv-list">
                                    <dt>New subs</dt>
                                    <dd>{charts.data.revenue.new_subs}</dd>
                                    <dt>Upgrades</dt>
                                    <dd>{charts.data.revenue.upgrades}</dd>
                                    <dt>Cancellations</dt>
                                    <dd className="text-warning">
                                        {charts.data.revenue.cancellations}
                                    </dd>
                                    <dt>Refunds</dt>
                                    <dd>{charts.data.revenue.refunds}</dd>
                                    <dt>Failed</dt>
                                    <dd className="text-danger">{charts.data.revenue.failed}</dd>
                                    <dt>Churn</dt>
                                    <dd>{charts.data.revenue.churn_pct.toFixed(1)}%</dd>
                                </dl>
                            )}
                        </div>
                    </section>

                    <section className="card">
                        <header className="card-header">
                            <h2 className="card-title">Top errors · 24h</h2>
                            <Link to="/error-logs" style={{ fontSize: 11 }}>
                                See all →
                            </Link>
                        </header>
                        <div className="card-body tight">
                            <div style={{ padding: '0 8px 8px' }}>
                                {charts.data?.top_errors.map((e) => (
                                    <Link
                                        key={e.code}
                                        to={`/error-logs?code=${encodeURIComponent(e.code)}`}
                                        style={{
                                            display: 'grid',
                                            gridTemplateColumns: '1fr auto',
                                            alignItems: 'center',
                                            padding: '8px 8px',
                                            borderRadius: 6,
                                            color: 'inherit',
                                            textDecoration: 'none'
                                        }}
                                    >
                                        <div style={{ minWidth: 0 }}>
                                            <div
                                                style={{
                                                    fontFamily: 'JetBrains Mono, monospace',
                                                    fontSize: 11,
                                                    color: 'var(--text-primary)'
                                                }}
                                            >
                                                {e.code}
                                            </div>
                                            <div
                                                style={{
                                                    fontSize: 10,
                                                    color: 'var(--text-muted)',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap'
                                                }}
                                            >
                                                {e.sample}
                                            </div>
                                        </div>
                                        <span
                                            className="chip"
                                            data-tone={
                                                e.count > 20
                                                    ? 'danger'
                                                    : e.count > 8
                                                      ? 'warning'
                                                      : 'muted'
                                            }
                                        >
                                            {e.count}
                                        </span>
                                    </Link>
                                ))}
                            </div>
                        </div>
                    </section>
                </div>
            </div>
        </div>
    );
}

function AlertIcon({ severity }: { severity: Severity }) {
    if (severity === 'danger') return <AlertCircle />;
    if (severity === 'warning') return <AlertTriangle />;
    return <Info />;
}

function toneForCategory(cat: ActivityCategory) {
    switch (cat) {
        case 'signup':
        case 'first_import':
            return 'info';
        case 'plan_upgrade':
        case 'license_sold':
            return 'success';
        case 'plan_downgrade':
            return 'warning';
        case 'payment_failed':
        case 'admin_destructive':
            return 'danger';
        case 'support_ticket':
            return 'warning';
        case 'share_created':
            return 'purple';
        default:
            return undefined;
    }
}

function ActivityIcon({ category }: { category: ActivityCategory }) {
    switch (category) {
        case 'signup':
            return <UserPlus />;
        case 'plan_upgrade':
        case 'plan_downgrade':
            return <CreditCard />;
        case 'first_import':
            return <Upload />;
        case 'share_created':
            return <Radio />;
        case 'license_sold':
            return <CheckCircle />;
        case 'payment_failed':
            return <AlertCircle />;
        case 'support_ticket':
            return <LifeBuoy />;
        case 'admin_destructive':
            return <ShieldAlert />;
        default:
            return <Info />;
    }
}
